import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, UserRound, Building2, UserX, UserCheck } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { userAdminService, rolesService, divisionService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import DetailPageHeader from '@components/common/DetailPageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { InfoCard, InfoCardGrid } from '@components/common';
import EntityActivity from '@components/common/EntityActivity';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import {
  ContactUserEditModal,
  PendingContactUserBadge,
  useActivateContactUser,
  useDeactivateContactUser
} from '@components/features/users';
import { formatPersonName } from '@utils/stringUtils';
import { formatInstantAsDate } from '@utils/dateUtils';
import { getContactStatusLabel } from '@utils/referenceCatalog';
import { getApiErrorStatus } from '@utils/apiError';
import { CONTACT_STATUS } from '@app-types/user';
import type { AdminUser } from '@app-types/admin';
import type { ContactUserField } from '@app-types/user';
import styles from './ContactUserDetailPage.module.css';

const EMPTY_VALUE = '—';

const orEmpty = (value: string | null | undefined): string => (value && value.trim() !== '' ? value : EMPTY_VALUE);

// Same pill as the User Management list's Status column.
const statusBadge = (isActive: boolean, label: string, dataTestId: string): React.ReactNode => (
  <Badge variant={isActive ? 'success' : 'error'} dataTestId={dataTestId}>
    {getContactStatusLabel(label)}
  </Badge>
);

// Labels match the edit form and the User Management list.
const contactFields = (user: AdminUser): ContactUserField[] => [
  { label: 'ContactID', value: orEmpty(user.contact_id) },
  { label: 'UserID', value: orEmpty(user.user_id) },
  { label: 'First Name', value: orEmpty(user.first_name) },
  { label: 'Last Name', value: orEmpty(user.last_name) },
  { label: 'Preferred Name', value: orEmpty(user.preferred_name) },
  { label: 'Email', value: orEmpty(user.email) },
  {
    label: 'Contact Status',
    value: user.contact_status
      ? statusBadge(
          user.contact_status === CONTACT_STATUS.ACTIVE,
          user.contact_status,
          'contact-user-contact-status-badge'
        )
      : EMPTY_VALUE
  },
  {
    label: 'Status',
    value: statusBadge(
      user.is_active,
      user.is_active ? CONTACT_STATUS.ACTIVE : CONTACT_STATUS.INACTIVE,
      'contact-user-status-badge'
    )
  },
  { label: 'Roles', value: user.role_names?.length ? user.role_names.join(', ') : EMPTY_VALUE }
];

const organizationFields = (user: AdminUser): ContactUserField[] => [
  { label: 'Department', value: orEmpty(user.department) },
  { label: 'Division', value: orEmpty(user.division_name) },
  { label: 'CostCenter', value: orEmpty(user.cost_center) },
  { label: 'Additional Contact Data 1', value: orEmpty(user.additional_contact_data1) },
  { label: 'Additional Contact Data 2', value: orEmpty(user.additional_contact_data2) },
  { label: 'Additional Contact Data 3', value: orEmpty(user.additional_contact_data3) },
  { label: 'Created', value: formatInstantAsDate(user.created_at, EMPTY_VALUE) },
  { label: 'Updated', value: formatInstantAsDate(user.updated_at, EMPTY_VALUE) }
];

const renderFields = (fields: ContactUserField[]) =>
  fields.map(({ label, value }) => (
    <InfoCard.Row key={label} data-test-id={`contact-user-field-${label}`}>
      <InfoCard.Label>{label}:</InfoCard.Label>
      <InfoCard.Value data-test-id="contact-user-field-value">{value}</InfoCard.Value>
    </InfoCard.Row>
  ));

/**
 * One Contact-User: the record's fields, and the Notes and History about them (CTU-7.4).
 */
const ContactUserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const isValidId = Number.isInteger(userId) && userId > 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Program Lead has no Notes or History here; its history is the exported Audit Log (ADO-1176)
  const { canManageUsers, canAccessContactUserRecordContext } = usePermissions();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const {
    data: user,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['contact-user', userId],
    queryFn: () => userAdminService.getUser(userId),
    enabled: isValidId
  });

  // The edit form's role and division options; only an Admin can open it.
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesService.getRoles(),
    enabled: canManageUsers
  });
  const { data: divisions = [] } = useQuery({
    queryKey: ['divisions', 'active'],
    queryFn: () => divisionService.getActive(),
    enabled: canManageUsers
  });

  const handleSaved = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['notes', 'user', userId] });
    queryClient.invalidateQueries({ queryKey: ['audit-history', 'user', userId] });
  };

  const deactivation = useDeactivateContactUser({ user, onDeactivated: handleSaved });
  const activation = useActivateContactUser({ user, onActivated: handleSaved });

  const goToList = () => navigate('/users');

  if (!isValidId || error) {
    const notFound = !isValidId || getApiErrorStatus(error) === 404;
    return (
      <PageLayout>
        <ErrorState
          title={notFound ? 'User Not Found' : 'Error'}
          message={
            notFound
              ? "The user you're looking for doesn't exist or has been deleted."
              : 'Error loading user details. Please try again.'
          }
          action={
            <Button variant="primary" onClick={goToList}>
              Go to User Management
            </Button>
          }
        />
      </PageLayout>
    );
  }

  if (isLoading || !user) {
    return (
      <PageLayout>
        <LoadingState message="Loading user details..." />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<UserRound size={24} />}
        title={formatPersonName(user)}
        subtitle={user.email}
        backLabel="Go Back"
        onBack={() => navigate(-1)}
        actions={
          <div className={styles.headerActions}>
            <Badge variant={user.is_active ? 'success' : 'error'}>{user.is_active ? 'Active' : 'Inactive'}</Badge>
            {user.is_pending_contact_user && <PendingContactUserBadge />}
            {canManageUsers && (
              <Button variant="secondary" size="sm" onClick={() => setIsEditOpen(true)} dataTestId="contact-user-edit">
                <Edit size={16} />
                Edit
              </Button>
            )}
            {canManageUsers && user.is_active && (
              <Button
                variant="danger"
                size="sm"
                onClick={deactivation.openConfirm}
                disabled={deactivation.isDisabled}
                title={deactivation.tooltip}
                dataTestId="contact-user-deactivate"
              >
                <UserX size={16} />
                Deactivate
              </Button>
            )}
            {canManageUsers && !user.is_active && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => activation.openConfirm()}
                disabled={activation.isDisabled}
                title={activation.tooltip}
                dataTestId="contact-user-activate"
              >
                <UserCheck size={16} />
                Activate
              </Button>
            )}
          </div>
        }
      />

      <div className="detail-content">
        <div className={styles.detailMain}>
          <InfoCardGrid>
            <InfoCard>
              <InfoCard.Header>
                <InfoCard.Title icon={<UserRound size={20} />}>Contact-User Information</InfoCard.Title>
              </InfoCard.Header>
              <InfoCard.Content>{renderFields(contactFields(user))}</InfoCard.Content>
            </InfoCard>
            <InfoCard>
              <InfoCard.Header>
                <InfoCard.Title icon={<Building2 size={20} />}>Organization</InfoCard.Title>
              </InfoCard.Header>
              <InfoCard.Content>{renderFields(organizationFields(user))}</InfoCard.Content>
            </InfoCard>
          </InfoCardGrid>

          {canAccessContactUserRecordContext && (
            <EntityActivity
              entityType="user"
              entityId={user.id}
              title="Contact-User Activity"
              canAddNote={canManageUsers}
            />
          )}
        </div>
      </div>

      {canManageUsers && (
        <ContactUserEditModal
          isOpen={isEditOpen}
          user={user}
          roles={roles}
          divisions={divisions}
          onClose={() => setIsEditOpen(false)}
          onSaved={handleSaved}
        />
      )}
      {canManageUsers && deactivation.confirmModal}
      {canManageUsers && activation.confirmModal}
    </PageLayout>
  );
};

export default ContactUserDetailPage;
